import { StatusMessageStoreTopic, type StatusMessage } from "@framework/types/statusWriter";
import type { PublishSubscribe } from "@lib/utils/PublishSubscribeDelegate";
import { PublishSubscribeDelegate } from "@lib/utils/PublishSubscribeDelegate";
import { UnsubscribeFunctionsManagerDelegate } from "@lib/utils/UnsubscribeFunctionsManagerDelegate";

import {
    type DataProviderManager,
    DataProviderManagerTopic,
    type GlobalSettings,
} from "../framework/DataProviderManager/DataProviderManager";
import type { SettingManager } from "../framework/SettingManager/SettingManager";
import { SettingTopic } from "../framework/SettingManager/SettingManager";
import type {
    CustomSettingsHandler,
    ResolverSpec,
    SettingAttributes,
    SetupBindingsContext,
    SharedResult,
} from "../interfacesAndTypes/customSettingsHandler";
import type { SerializedSettingsState } from "../interfacesAndTypes/serialization";
import type { NullableStoredData, StoredData } from "../interfacesAndTypes/sharedTypes";
import type { MakeSettingTypesMap, SettingsKeysFromTuple } from "../interfacesAndTypes/utils";
import type { Settings, SettingTypeDefinitions } from "../settings/settingsDefinitions";

import { assertNoCircularValueConstraints } from "./_utils/assertNoCircularValueConstraints";
import { Dependency, type Read } from "./_utils/Dependency";

export enum SettingsContextStatus {
    VALID_SETTINGS = "VALID_SETTINGS",
    LOADING = "LOADING",
    INVALID_SETTINGS = "INVALID_SETTINGS",
}

export enum SettingsContextDelegateTopic {
    SETTINGS_AND_STORED_DATA_CHANGED = "SETTINGS_AND_STORED_DATA_CHANGED",
    STATUS = "STATUS",
    STATUS_MESSAGES = "STATUS_MESSAGES",
}

export type SettingsContextDelegatePayloads = {
    [SettingsContextDelegateTopic.SETTINGS_AND_STORED_DATA_CHANGED]: void;
    [SettingsContextDelegateTopic.STATUS]: SettingsContextStatus;
    [SettingsContextDelegateTopic.STATUS_MESSAGES]: readonly StatusMessage[];
};

export interface FetchDataFunction<TSettings extends Settings, TKey extends keyof TSettings> {
    (oldValues: { [K in TKey]: TSettings[K] }, newValues: { [K in TKey]: TSettings[K] }): void;
}

export type SettingsContextDelegateState<TSettings extends Settings, TKey extends keyof TSettings> = {
    values: { [K in TKey]: TSettings[K] };
};

/*
 * The SettingsContextDelegate class is responsible for giving the settings of a data provider a common context as
 * many settings are interdependent.
 *
 * It creates a dependency graph for all settings and implements dependencies between both themselves and global settings.
 * It also takes care of notifying its subscribers (e.g. the respective data provider) when the settings change.
 *
 */
export class SettingsContextDelegate<
    TSettings extends Settings,
    TSettingTypes extends MakeSettingTypesMap<TSettings> = MakeSettingTypesMap<TSettings>,
    TStoredData extends StoredData = Record<string, never>,
    TSettingKey extends SettingsKeysFromTuple<TSettings> = SettingsKeysFromTuple<TSettings>,
    TStoredDataKey extends keyof TStoredData = keyof TStoredData,
> implements PublishSubscribe<SettingsContextDelegatePayloads> {
    private _customSettingsHandler: CustomSettingsHandler<
        TSettings,
        TStoredData,
        TSettingTypes,
        TSettingKey,
        TStoredDataKey
    >;
    private _dataProviderManager: DataProviderManager;
    private _settings: {
        [K in TSettingKey]: SettingManager<K>;
    } = {} as {
        [K in TSettingKey]: SettingManager<K>;
    };
    private _publishSubscribeDelegate = new PublishSubscribeDelegate<SettingsContextDelegatePayloads>();
    private _unsubscribeFunctionsManagerDelegate: UnsubscribeFunctionsManagerDelegate =
        new UnsubscribeFunctionsManagerDelegate();
    private _status: SettingsContextStatus = SettingsContextStatus.LOADING;
    private _storedData: NullableStoredData<TStoredData> = {} as NullableStoredData<TStoredData>;
    private _storedDataLoadingStatus: { [K in TStoredDataKey]: boolean } = {} as {
        [K in TStoredDataKey]: boolean;
    };
    private _dependencies: Dependency<any, TSettings, any, any, any>[] = [];

    private _dependencyStatusMessages: StatusMessage[] = [];

    constructor(
        customSettingsHandler: CustomSettingsHandler<TSettings, TStoredData, TSettingTypes, TSettingKey>,
        dataProviderManager: DataProviderManager,
        settings: { [K in TSettingKey]: SettingManager<K> },
    ) {
        this._customSettingsHandler = customSettingsHandler;
        this._dataProviderManager = dataProviderManager;

        this._settings = settings;

        // Global settings are not subscribed to here - changes reach the settings through the dependencies that read them
        for (const key in this._settings) {
            this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
                "settings",
                this._settings[key].getPublishSubscribeDelegate().makeSubscriberFunction(SettingTopic.VALUE)(() => {
                    this.handleSettingChanged();
                }),
            );
            this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
                "settings",
                this._settings[key].getPublishSubscribeDelegate().makeSubscriberFunction(SettingTopic.IS_LOADING)(
                    () => {
                        this.handleSettingChanged();
                    },
                ),
            );
            this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
                "settings",
                this._settings[key].getPublishSubscribeDelegate().makeSubscriberFunction(SettingTopic.IS_BLOCKED)(
                    () => {
                        this.handleSettingChanged();
                    },
                ),
            );
            // Whether a setting is shown decides whether its validity counts
            this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
                "settings",
                this._settings[key].getPublishSubscribeDelegate().makeSubscriberFunction(SettingTopic.ATTRIBUTES)(
                    () => {
                        this.handleSettingChanged();
                    },
                ),
            );
        }

        this.setupDependencies();
    }

    getDataProviderManager(): DataProviderManager {
        return this._dataProviderManager;
    }

    /*
     * The settings are evaluated whenever a setting or dependency changes - without any dependencies, that may never
     * happen, which would leave the owner waiting forever. The owner calls this once it has subscribed.
     */
    evaluateIfWithoutDependencies(): void {
        if (this._dependencies.length === 0) {
            this.handleSettingChanged();
        }
    }

    getStatus(): SettingsContextStatus {
        return this._status;
    }

    getStatusMessages(): readonly StatusMessage[] {
        return this._dependencyStatusMessages;
    }

    /*
     * The values that decide whether the provider's data is outdated. A persisted value that is still held has not been
     * adopted yet, and a hidden setting does not apply to the current configuration (e.g. a shared setting may still
     * change it) - both are left undefined, so that changing them never makes the provider refetch.
     */
    getValues(): { [K in TSettingKey]?: TSettingTypes[K] } {
        const settings: { [K in TSettingKey]?: TSettingTypes[K] } = {} as {
            [K in TSettingKey]?: TSettingTypes[K];
        };
        for (const key in this._settings) {
            if (this._settings[key].isPersistedValue() || !this.isSettingApplicable(key)) {
                settings[key] = undefined;
                continue;
            }
            settings[key] = this._settings[key].getValue() as any;
        }

        return settings;
    }

    /*
     * A hidden setting does not apply to the current configuration (e.g. a filter that is switched off) - the user can
     * neither see nor fix it, so it must not make the settings invalid.
     * A disabled setting that is shown still counts, as it explains itself (e.g. "No surfaces available").
     */
    private isSettingApplicable(key: TSettingKey): boolean {
        return this._settings[key].getAttributes().visible;
    }

    private isSettingInvalid(key: TSettingKey): boolean {
        if (!this.isSettingApplicable(key)) {
            return false;
        }
        return !this._settings[key].isValueValid() || this._settings[key].isPersistedValue();
    }

    // A blocked setting can't be checked against up-to-date value constraints, so it does not count as valid either
    areCurrentSettingsValid(): boolean {
        for (const key in this._settings) {
            if (
                this.isSettingApplicable(key) &&
                (!this._settings[key].isValueValid() || this._settings[key].isBlocked())
            ) {
                return false;
            }
        }

        return true;
    }

    areAllDependenciesLoaded(): boolean {
        for (const dependency of this._dependencies) {
            if (dependency.getIsLoading()) {
                return false;
            }
        }

        return true;
    }

    areAllSettingsLoaded(): boolean {
        for (const key in this._settings) {
            if (this._settings[key].isLoading()) {
                return false;
            }
        }

        return true;
    }

    isAllStoredDataLoaded(): boolean {
        for (const key in this._storedDataLoadingStatus) {
            if (this._storedDataLoadingStatus[key]) {
                return false;
            }
        }

        return true;
    }

    // A blocked setting may never have been initialized - it is settled nonetheless, as it waits for another one
    areAllSettingsInitialized(): boolean {
        for (const key in this._settings) {
            if (!this._settings[key].isInitialized() && !this._settings[key].isBlocked()) {
                return false;
            }
        }

        return true;
    }

    /*
     * A persisted value is adopted as soon as value constraints that accept it arrive. One that is still held once the
     * settings are initialized has either been rejected by the constraints or failed to deserialize, and stays until the
     * user changes the setting.
     */
    isSomePersistedValueUnresolved(): boolean {
        for (const key in this._settings) {
            if (this.isSettingApplicable(key) && this._settings[key].isPersistedValue()) {
                return true;
            }
        }

        return false;
    }

    // The labels of the shown settings that make the settings invalid, including those still holding a rejected persisted value
    getInvalidSettings(): string[] {
        const invalidSettings: string[] = [];
        for (const key in this._settings) {
            if (this.isSettingInvalid(key)) {
                invalidSettings.push(this._settings[key].getLabel());
            }
        }

        return invalidSettings;
    }

    setValueConstraints<K extends TSettingKey>(
        key: K,
        valueConstraints: SettingTypeDefinitions[K]["valueConstraints"],
    ): void {
        const settingDelegate = this._settings[key];
        settingDelegate.setValueConstraints(valueConstraints);
    }

    setStoredData<K extends TStoredDataKey>(key: K, data: TStoredData[K] | null): void {
        this._storedData[key] = data;
        this._storedDataLoadingStatus[key] = false;

        this.handleSettingChanged();
    }

    getSettings() {
        return this._settings;
    }

    getStoredDataRecord(): NullableStoredData<TStoredData> {
        return this._storedData;
    }

    getStoredData(key: TStoredDataKey): TStoredData[TStoredDataKey] | null {
        return this._storedData[key];
    }

    makeSnapshotGetter<T extends SettingsContextDelegateTopic>(topic: T): () => SettingsContextDelegatePayloads[T] {
        const snapshotGetter = (): any => {
            if (topic === SettingsContextDelegateTopic.SETTINGS_AND_STORED_DATA_CHANGED) {
                return;
            }
            if (topic === SettingsContextDelegateTopic.STATUS) {
                return this._status;
            }
            if (topic === SettingsContextDelegateTopic.STATUS_MESSAGES) {
                return this._dependencyStatusMessages;
            }
        };

        return snapshotGetter;
    }

    getPublishSubscribeDelegate(): PublishSubscribeDelegate<SettingsContextDelegatePayloads> {
        return this._publishSubscribeDelegate;
    }

    serializeSettings(): SerializedSettingsState<TSettings, TSettingKey> {
        const serializedSettings: SerializedSettingsState<TSettings, TSettingKey> = {} as SerializedSettingsState<
            TSettings,
            TSettingKey
        >;
        for (const key in this._settings) {
            serializedSettings[key] = this._settings[key].serializeValue();
        }
        return serializedSettings;
    }

    deserializeSettings(
        serializedSettings: SerializedSettingsState<TSettings, TSettingKey>,
        reportError: (errorMsg: string) => void,
    ): void {
        for (const [key, value] of Object.entries(serializedSettings)) {
            const settingDelegate = this._settings[key as TSettingKey];

            // Temporary skip undefined settingsDelegate (await persistence versioning)
            // - Setting might have been removed since creation of the serialized state (e.g. session).
            if (settingDelegate === undefined) {
                reportError(`Setting with key '${key}' does not exist anymore. Cannot apply saved value.`);
                continue;
            }

            settingDelegate.deserializeValue(value as string);
            if (settingDelegate.isStatic()) {
                settingDelegate.maybeResetPersistedValue();
                settingDelegate.initialize();
            }
        }
    }

    setupDependencies(): void {
        this._unsubscribeFunctionsManagerDelegate.unsubscribe("dependencies");

        this._dependencies = [];
        const valueConstraintsDependencies = new Map<string, Dependency<any, TSettings, any, any, any>[]>();

        const makeLocalSettingGetter = <K extends TSettingKey>(key: K, handler: (value: TSettingTypes[K]) => void) => {
            const handleChange = (): void => {
                const setting = this._settings[key];
                handler(setting.getValue() as unknown as TSettingTypes[K]);
            };
            this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
                "dependencies",
                this._settings[key].getPublishSubscribeDelegate().makeSubscriberFunction(SettingTopic.VALUE)(
                    handleChange,
                ),
            );

            this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
                "dependencies",
                this._settings[key].getPublishSubscribeDelegate().makeSubscriberFunction(SettingTopic.IS_LOADING)(
                    () => {
                        if (!this._settings[key].isLoading()) {
                            handleChange();
                        }
                    },
                ),
            );

            this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
                "dependencies",
                this._settings[key].getPublishSubscribeDelegate().makeSubscriberFunction(SettingTopic.IS_PERSISTED)(
                    handleChange,
                ),
            );

            this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
                "dependencies",
                this._settings[key].getPublishSubscribeDelegate().makeSubscriberFunction(SettingTopic.IS_INITIALIZED)(
                    handleChange,
                ),
            );

            return handleChange;
        };

        const makeGlobalSettingGetter = <K extends keyof GlobalSettings>(
            key: K,
            handler: (value: GlobalSettings[K] | null) => void,
        ) => {
            const handleChange = (): void => {
                handler(this.getDataProviderManager.bind(this)().getGlobalSetting(key));
            };
            this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
                "dependencies",
                this.getDataProviderManager()
                    .getPublishSubscribeDelegate()
                    .makeSubscriberFunction(DataProviderManagerTopic.GLOBAL_SETTINGS)(handleChange),
            );

            return handleChange;
        };

        const loadingStateGetter = <K extends TSettingKey>(settingKey: K): boolean => {
            return this._settings[settingKey].isLoading();
        };

        const localSettingManagerGetter = <K extends TSettingKey>(key: K): SettingManager<K> => {
            return this._settings[key];
        };

        const globalSettingGetter = <K extends keyof GlobalSettings>(key: K): GlobalSettings[K] | null => {
            return this.getDataProviderManager.bind(this)().getGlobalSetting(key);
        };

        const globalSettingValidityGetter = (key: keyof GlobalSettings): boolean => {
            return this.getDataProviderManager.bind(this)().isGlobalSettingValid(key);
        };

        const createDependency = <T, TReads extends Record<string, Read<any>> = Record<string, never>>(
            debugName: string,
            resolverSpec: ResolverSpec<T, TSettings, TSettingTypes, TSettingKey, TReads>,
        ): Dependency<T, TSettings, TSettingTypes, TSettingKey, TReads> => {
            const dependency = new Dependency<T, TSettings, TSettingTypes, TSettingKey, TReads>(
                localSettingManagerGetter,
                globalSettingGetter,
                resolverSpec,
                makeLocalSettingGetter,
                loadingStateGetter,
                makeGlobalSettingGetter,
                debugName,
                globalSettingValidityGetter,
            );
            this._dependencies.push(dependency);
            dependency.initialize();
            return dependency;
        };

        const bindValueConstraints = <
            K extends TSettingKey,
            TReads extends Record<string, Read<any>> = Record<string, never>,
        >(
            settingKey: K,
            resolverSpec: ResolverSpec<
                SettingTypeDefinitions[K]["valueConstraints"],
                TSettings,
                TSettingTypes,
                TSettingKey,
                TReads
            >,
        ): Dependency<SettingTypeDefinitions[K]["valueConstraints"], TSettings, TSettingTypes, TSettingKey, TReads> => {
            const debugName = `ValueConstraintsUpdater_${settingKey}`;
            const dependency = createDependency(debugName, resolverSpec);
            valueConstraintsDependencies.set(settingKey, [
                ...(valueConstraintsDependencies.get(settingKey) ?? []),
                dependency,
            ]);

            dependency.subscribe((valueConstraints) => {
                if (valueConstraints === null) {
                    this.setValueConstraints(settingKey, null as SettingTypeDefinitions[K]["valueConstraints"]);
                    return;
                }
                this.setValueConstraints(settingKey, valueConstraints);
                this.handleSettingChanged();
            });

            dependency.subscribeLoading((loading: boolean) => {
                if (loading) {
                    this._settings[settingKey].setLoading(loading);
                }
                this.handleSettingChanged();
            });

            // The setting keeps its value and constraints while it waits - blocked before it stops loading, so whatever
            // reacts to it no longer loading sees it blocked already
            dependency.subscribeBlocked((blockedBy) => {
                this._settings[settingKey].setBlocked(true, blockedBy);
                this._settings[settingKey].setLoading(false);
                this.handleSettingChanged();
            });

            this.subscribeToDependencyStatusMessages(dependency);

            return dependency;
        };

        const bindAttributes = <
            K extends TSettingKey,
            TReads extends Record<string, Read<any>> = Record<string, never>,
        >(
            settingKey: K,
            resolverSpec: ResolverSpec<Partial<SettingAttributes>, TSettings, TSettingTypes, TSettingKey, TReads>,
        ): Dependency<Partial<SettingAttributes>, TSettings, TSettingTypes, TSettingKey, TReads> => {
            const debugName = `SettingAttributesUpdater_${settingKey}`;
            const markAttributesResolved = this._settings[settingKey].registerAttributesBinding();
            const dependency = createDependency(debugName, resolverSpec);

            dependency.subscribe((attributes: Partial<SettingAttributes> | null) => {
                // A failed resolver leaves the default attributes, rather than keeping the setting from ever being shown
                const attributesChanged =
                    attributes !== null && this._settings[settingKey].updateAttributes(attributes);
                markAttributesResolved();
                // Changed attributes are re-evaluated through the ATTRIBUTES subscription already
                if (!attributesChanged) {
                    this.handleSettingChanged();
                }
            });

            // Only the start of loading is handled here. The end is published before the resolved attributes are
            // applied, so evaluating then would judge the settings by the old attributes - e.g. find them invalid
            // because of a setting that is about to be hidden, which would end a restore before the provider loads.
            // The value subscriber above evaluates once the attributes are applied instead.
            dependency.subscribeLoading((loading) => {
                if (loading) {
                    this.handleSettingChanged();
                }
            });

            // The current attributes stay - while it waits, the setting must neither disappear nor hold up a restore
            dependency.subscribeBlocked(() => {
                markAttributesResolved();
                this.handleSettingChanged();
            });

            this.subscribeToDependencyStatusMessages(dependency);

            return dependency;
        };

        const bindStoredData = <
            K extends TStoredDataKey,
            TReads extends Record<string, Read<any>> = Record<string, never>,
        >(
            key: K,
            resolverSpec: ResolverSpec<
                NullableStoredData<TStoredData>[K],
                TSettings,
                TSettingTypes,
                TSettingKey,
                TReads
            >,
        ): Dependency<NullableStoredData<TStoredData>[K], TSettings, TSettingTypes, TSettingKey, TReads> => {
            const debugName = `StoredDataUpdater_${String(key)}`;
            const dependency = createDependency(debugName, resolverSpec);

            dependency.subscribe((storedData: TStoredData[K] | null) => {
                this.setStoredData(key, storedData);
            });

            dependency.subscribeLoading((loading: boolean) => {
                if (loading) {
                    this._storedData[key] = null;
                    this._storedDataLoadingStatus[key] = loading;
                    this.handleSettingChanged();
                }
            });

            // Stays empty, as it was cleared when it started loading
            dependency.subscribeBlocked(() => {
                this._storedDataLoadingStatus[key] = false;
                this.handleSettingChanged();
            });

            this.subscribeToDependencyStatusMessages(dependency);

            return dependency;
        };

        const makeSharedResult = <T, TReads extends Record<string, Read<any>> = Record<string, never>>(
            args: ResolverSpec<T, TSettings, TSettingTypes, TSettingKey, TReads> & { debugName: string },
        ) => {
            const { debugName, ...resolverSpec } = args;
            const dependency = createDependency(debugName, resolverSpec);

            dependency.subscribeLoading(() => {
                this.handleSettingChanged();
            });
            dependency.subscribeBlocked(() => {
                this.handleSettingChanged();
            });

            this.subscribeToDependencyStatusMessages(dependency);
            return dependency as SharedResult<T, TSettings, TSettingTypes, TSettingKey, TReads>;
        };

        const context: SetupBindingsContext<TSettings, TStoredData, TSettingTypes, TSettingKey, TStoredDataKey> = {
            setting: <K extends TSettingKey>(settingKey: K) => ({
                bindValueConstraints: <TReads extends Record<string, Read<any>> = Record<string, never>>(
                    resolverSpec: ResolverSpec<
                        SettingTypeDefinitions[K]["valueConstraints"],
                        TSettings,
                        TSettingTypes,
                        TSettingKey,
                        TReads
                    >,
                ) => bindValueConstraints(settingKey, resolverSpec),
                bindAttributes: <TReads extends Record<string, Read<any>> = Record<string, never>>(
                    resolverSpec: ResolverSpec<
                        Partial<SettingAttributes>,
                        TSettings,
                        TSettingTypes,
                        TSettingKey,
                        TReads
                    >,
                ) => bindAttributes(settingKey, resolverSpec),
            }),

            storedData: <K extends TStoredDataKey>(key: K) => ({
                bindValue: <TReads extends Record<string, Read<any>> = Record<string, never>>(
                    resolverSpec: ResolverSpec<
                        NullableStoredData<TStoredData>[K],
                        TSettings,
                        TSettingTypes,
                        TSettingKey,
                        TReads
                    >,
                ) => bindStoredData(key, resolverSpec),
            }),

            makeSharedResult,

            workbenchSession: this.getDataProviderManager().getWorkbenchSession(),
            workbenchSettings: this.getDataProviderManager().getWorkbenchSettings(),
            queryClient: this.getDataProviderManager().getQueryClient(),
        };

        if (this._customSettingsHandler.setupBindings) {
            this._customSettingsHandler.setupBindings(context);
        }

        // Settings blocking each other would wait for each other for good - see Dependency
        assertNoCircularValueConstraints(valueConstraintsDependencies, (settingKey) =>
            this._settings[settingKey as TSettingKey].getLabel(),
        );
    }

    beforeDestroy(): void {
        this._unsubscribeFunctionsManagerDelegate.unsubscribeAll();
        for (const dependency of this._dependencies) {
            dependency.beforeDestroy();
        }
        this._dependencies = [];
        for (const key in this._settings) {
            this._settings[key].beforeDestroy();
        }
        this._settings = {} as {
            [K in TSettingKey]: SettingManager<K>;
        };
    }

    private setStatus(status: SettingsContextStatus) {
        if (this._status === status) {
            return;
        }

        this._status = status;
        this._publishSubscribeDelegate.notifySubscribers(SettingsContextDelegateTopic.STATUS);
    }

    private handleSettingChanged() {
        if (
            !this.areAllSettingsLoaded() ||
            !this.areAllDependenciesLoaded() ||
            !this.isAllStoredDataLoaded() ||
            !this.areAllSettingsInitialized()
        ) {
            this.setStatus(SettingsContextStatus.LOADING);
            return;
        }

        if (!this.areCurrentSettingsValid() || this.isSomePersistedValueUnresolved()) {
            this.setStatus(SettingsContextStatus.INVALID_SETTINGS);
            return;
        }

        this.setStatus(SettingsContextStatus.VALID_SETTINGS);
        this._publishSubscribeDelegate.notifySubscribers(SettingsContextDelegateTopic.SETTINGS_AND_STORED_DATA_CHANGED);
    }

    private subscribeToDependencyStatusMessages(dependency: Dependency<any, any, any, any, any>): void {
        dependency
            .getStatusMessageStore()
            .getPublishSubscribeDelegate()
            .subscribe(StatusMessageStoreTopic.STATUS_MESSAGES, () => this.syncAllStatusMessages());
    }

    private syncAllStatusMessages(): void {
        this._dependencyStatusMessages = this._dependencies.flatMap((d) => d.getStatusMessages());

        this._publishSubscribeDelegate.notifySubscribers(SettingsContextDelegateTopic.STATUS_MESSAGES);
    }
}
